import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';

/**
 * <LoadingVideo> — a <video> that hides the browser's default grey play-button
 * poster behind a dark loading state until real frames start rendering.
 *
 * Mirrors the pattern used by VideoSlide inside components/guest-map/GuestMapSurface.js
 * so event videos on the guest map, venue events list, and venue event create
 * preview all get the same branded loading treatment.
 *
 * Props:
 *   - src (string, required): resolved video URL.
 *   - style (object): applied to the <video> element.
 *   - wrapperStyle (object): applied to the wrapping div (position:relative).
 *   - compact (bool): when true, hides the "Loading…" label so the overlay
 *       fits inside small thumbnails (e.g. 56x56 cells).
 *   - spinnerSize (number): px, default 30 (full) / 14 (compact).
 *   - overlayBg (string): background of the dark overlay, default #0F1118.
 *   - dataTestId (string): forwarded to the <video>.
 *   - autoPlay, loop, muted, playsInline: forwarded to the <video>.
 */
export function LoadingVideo({
  src,
  style,
  wrapperStyle,
  compact = false,
  spinnerSize,
  overlayBg = '#0F1118',
  dataTestId,
  autoPlay = true,
  loop = true,
  muted = true,
  playsInline = true,
  preload = 'auto',
  ...videoProps
}) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const resolvedSpinner = spinnerSize ?? (compact ? 14 : 30);
  const spinnerBorder = compact ? 2 : 2.5;

  return (
    <div
      style={{
        position: 'relative',
        overflow: 'hidden',
        background: overlayBg,
        ...(wrapperStyle || {}),
      }}
    >
      <video
        ref={videoRef}
        src={src}
        muted={muted}
        loop={loop}
        autoPlay={autoPlay}
        playsInline={playsInline}
        preload={preload}
        disablePictureInPicture
        disableRemotePlayback
        data-testid={dataTestId}
        onPlaying={() => setPlaying(true)}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          pointerEvents: 'none',
          userSelect: 'none',
          transform: 'translateZ(0)',
          willChange: 'transform',
          backfaceVisibility: 'hidden',
          ...(style || {}),
        }}
        {...videoProps}
      />
      {/* Dark loading overlay — fades out on first `playing` event */}
      <div
        aria-hidden="true"
        data-testid={dataTestId ? `${dataTestId}-overlay` : undefined}
        style={{
          position: 'absolute',
          inset: 0,
          background: overlayBg,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: compact ? 0 : 10,
          opacity: playing ? 0 : 1,
          transition: 'opacity 380ms ease',
          pointerEvents: 'none',
        }}
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1.1, repeat: Infinity, ease: 'linear' }}
          style={{
            width: resolvedSpinner,
            height: resolvedSpinner,
            borderRadius: '50%',
            border: `${spinnerBorder}px solid rgba(255,255,255,0.14)`,
            borderTopColor: 'rgba(255,255,255,0.85)',
            boxSizing: 'border-box',
          }}
        />
        {!compact ? (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: [0.4, 0.9, 0.4] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
            style={{
              fontFamily: "'Inria Serif', serif",
              fontSize: 12.5,
              fontWeight: 600,
              color: 'rgba(255,255,255,0.72)',
              margin: 0,
              letterSpacing: '0.02em',
            }}
          >
            Loading…
          </motion.p>
        ) : null}
      </div>
    </div>
  );
}

export default LoadingVideo;
